// ── PayrollSummary — 월별 급여 명세 + 손익관리 자동 반영 + 거래장부 자동 입력 ────
import React, { useState, useMemo } from 'react';
import { calcMonthlyPayroll, DEFAULT_PAYROLL_SETTINGS } from '../../utils/payroll';
import { syncPayrollToLedger } from '../../utils/payrollLedgerSync';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

const DEDUCT_LABEL = {
  withholding3_3: '3.3% 원천징수',
  withholdingDaily: '일용직 6.6% 원천징수',
  dailyIncomeTax: '소득세 (일용직 6%)',
  dailyLocalTax: '지방소득세 (0.6%)',
  nationalPension: '국민연금',
  healthInsurance: '건강보험',
  longTermCare: '장기요양',
  employmentInsurance: '고용보험',
  incomeTax: '근로소득세',
  localTax: '지방소득세',
};

export default function PayrollSummary({
  employees = [], schedules = {}, settings = DEFAULT_PAYROLL_SETTINGS,
  profitLoss = {}, onProfitLossChange,
  user,
}) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [includeEmployerInsurance, setIncludeEmployerInsurance] = useState(false);
  const [showAllPrint, setShowAllPrint] = useState(false);
  const [ledgerSyncing, setLedgerSyncing] = useState(false);

  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  const monthData = schedules[monthKey] || {};
  const activeEmps = employees.filter(e => e.active !== false);

  const payrolls = useMemo(() => {
    return activeEmps.map(emp => {
      const monthShifts = [];
      for (let d = 1; d <= 31; d++) {
        const sh = monthData?.[d]?.[emp.id];
        if (sh) {
          monthShifts.push({
            date: `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
            ...sh,
          });
        }
      }
      return calcMonthlyPayroll(emp, monthShifts, settings);
    }).filter(Boolean);
  }, [activeEmps, monthData, year, month, settings]);

  const totalEarnings = payrolls.reduce((s, p) => s + (p.totalEarnings || 0), 0);
  const totalDeductions = payrolls.reduce((s, p) => s + (p.totalDeduction || 0), 0);
  const totalNet = payrolls.reduce((s, p) => s + (p.netPay || 0), 0);

  // 사업주 부담 4대보험 추정 (4대보험 가입 직원만)
  // 국민연금 4.5% + 건강보험 3.545% + 장기요양(건강의 12.95%) + 고용보험 ~0.9% + 산재 ~1.0%
  const employerInsurance = useMemo(() => {
    let total = 0;
    for (const p of payrolls) {
      const emp = activeEmps.find(e => e.id === p.employeeId);
      if (emp?.fourInsurance?.enabled) {
        // 본인부담률과 동일하게 사업주도 부담 (산재는 사업주만 1.0%)
        const np = Math.round(p.totalEarnings * 0.045);
        const hi = Math.round(p.totalEarnings * 0.03545);
        const ltc = Math.round(hi * 0.1295);
        const ei = Math.round(p.totalEarnings * 0.009);
        const wc = Math.round(p.totalEarnings * 0.01);
        total += np + hi + ltc + ei + wc;
      } else if (emp?.type !== 'freelancer') {
        // 알바도 산재보험은 사업주 부담
        total += Math.round(p.totalEarnings * 0.01);
      }
    }
    return total;
  }, [payrolls, activeEmps]);

  const totalLaborCost = totalEarnings + (includeEmployerInsurance ? employerInsurance : 0);
  const currentLabor = profitLoss?.[monthKey]?.costs?.labor || 0;

  const moveMonth = delta => {
    let m = month + delta, y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setYear(y); setMonth(m);
  };

  const syncToProfitLoss = () => {
    if (!onProfitLossChange) { alert('손익관리 연동 안 됨'); return; }
    const existingMonth = profitLoss?.[monthKey] || {};
    const existingRevenue = { card: 0, cash: 0, delivery: 0, ...(existingMonth.revenue || {}) };
    const existingCosts = { ingredients: 0, labor: 0, rent: 0, utilities: 0, other: 0, ...(existingMonth.costs || {}) };
    // V171: 손익에서 사람별로 넣은 인건비가 있으면 물어보고, 그 달의 다른 값(메모·사람별 등)은 지우지 않는다
    const { laborItems, ...keep } = existingMonth;
    if (Array.isArray(laborItems) && laborItems.length && !window.confirm(`손익에 사람별 인건비(${laborItems.length}명)가 있어요.\n사람별 입력을 지우고 급여 계산 합계 ${fmt(totalLaborCost)}원으로 바꿀까요?`)) return;
    const next = {
      ...(profitLoss || {}),
      [monthKey]: {
        ...keep,
        revenue: existingRevenue,
        costs: { ...existingCosts, labor: totalLaborCost },
        memo: existingMonth.memo || '',
      },
    };
    onProfitLossChange(next);
    alert(`✅ ${month}월 손익관리에 인건비 ${fmt(totalLaborCost)}원 반영 완료`);
  };

  const syncToLedger = async () => {
    if (!user?.uid) { alert('로그인이 필요해요.'); return; }
    if (payrolls.length === 0) { alert('이 달 급여 데이터가 없어요. 직원·스케줄 입력 후 다시 시도하세요.'); return; }
    const confirmMsg =
      `${year}년 ${month}월 급여를 거래장부에 자동 입력합니다.\n\n` +
      `• 직원 ${payrolls.length}명 × 급여 거래\n` +
      (includeEmployerInsurance && employerInsurance > 0
        ? `• 사업주부담 4대보험 ${fmt(employerInsurance)}원\n`
        : '') +
      `\n같은 월에 자동 입력 이력이 있으면 금액만 업데이트됩니다 (중복 방지).\n계속할까요?`;
    if (!window.confirm(confirmMsg)) return;
    setLedgerSyncing(true);
    try {
      const result = await syncPayrollToLedger(
        user.uid,
        monthKey,
        payrolls,
        activeEmps,
        includeEmployerInsurance ? employerInsurance : 0,
        settings,
      );
      const errMsg = result.errors.length > 0
        ? `\n⚠️ 일부 실패: ${result.errors.length}건 (콘솔 확인)`
        : '';
      alert(
        `✅ 거래장부 자동 입력 완료\n\n` +
        `• 신규 추가: ${result.added}건\n` +
        `• 업데이트: ${result.updated}건\n` +
        (result.skipped > 0 ? `• 건너뜀: ${result.skipped}건\n` : '') +
        errMsg
      );
    } catch (e) {
      console.error('syncToLedger error:', e);
      alert('거래장부 입력 실패: ' + (e.message || String(e)));
    } finally {
      setLedgerSyncing(false);
    }
  };

  const selectedPayroll = selectedEmpId ? payrolls.find(p => p.employeeId === selectedEmpId) : null;
  const selectedEmp = selectedEmpId ? activeEmps.find(e => e.id === selectedEmpId) : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '14px' }}>
        <button onClick={() => moveMonth(-1)}
          style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e5e7eb',
                   background: '#fff', cursor: 'pointer', fontSize: '13px' }}>‹</button>
        <strong style={{ fontSize: '15px', minWidth: '90px', textAlign: 'center' }}>
          {year}년 {month}월
        </strong>
        <button onClick={() => moveMonth(1)}
          style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e5e7eb',
                   background: '#fff', cursor: 'pointer', fontSize: '13px' }}>›</button>
      </div>

      <div style={{ background: '#FF6F0F', color: '#fff', borderRadius: '10px',
                    padding: '14px 16px', marginBottom: '14px', display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', textAlign: 'center',
                    overflow: 'hidden' }}>
        <div>
          <div style={{ fontSize: '11px', opacity: 0.85 }}>총 지급액</div>
          <div style={{ fontSize: '16px', fontWeight: '700', marginTop: '4px' }}>{fmt(totalEarnings)}원</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', opacity: 0.85 }}>총 공제</div>
          <div style={{ fontSize: '16px', fontWeight: '700', marginTop: '4px' }}>{fmt(totalDeductions)}원</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', opacity: 0.85 }}>실수령 합계</div>
          <div style={{ fontSize: '16px', fontWeight: '700', marginTop: '4px', color: '#A1A1AA' }}>{fmt(totalNet)}원</div>
        </div>
      </div>

      {/* 손익관리 자동 반영 */}
      {onProfitLossChange && (
        <div style={{ background: '#FAFAFA', border: '1px solid #F4F4F5', borderRadius: '10px',
                      padding: '14px', marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#18181B', marginBottom: '8px' }}>
            📊 손익관리 인건비 자동 반영
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px',
                          color: '#374151', marginBottom: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeEmployerInsurance}
              onChange={e => setIncludeEmployerInsurance(e.target.checked)} />
            <span>사업주 부담 4대보험·산재 포함 (+{fmt(employerInsurance)}원)</span>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
                        fontSize: '12px', marginBottom: '10px' }}>
            <div style={{ padding: '8px 10px', background: '#fff', borderRadius: '6px',
                          border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '10px', color: '#6b7280' }}>현재 손익관리 인건비</div>
              <div style={{ fontWeight: '700', color: '#374151', marginTop: '2px' }}>
                {fmt(currentLabor)}원
              </div>
            </div>
            <div style={{ padding: '8px 10px', background: '#fff', borderRadius: '6px',
                          border: '1px solid #FF6F0F' }}>
              <div style={{ fontSize: '10px', color: '#18181B' }}>반영할 금액 (이번 달 인건비)</div>
              <div style={{ fontWeight: '700', color: '#18181B', marginTop: '2px' }}>
                {fmt(totalLaborCost)}원
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={syncToProfitLoss}
              disabled={payrolls.length === 0}
              style={{ flex: 1, minWidth: 200, padding: '10px', borderRadius: '6px', border: 'none',
                       background: payrolls.length === 0 ? '#d1d5db' : '#FF6F0F',
                       color: '#fff', fontSize: '13px', fontWeight: '700',
                       cursor: payrolls.length === 0 ? 'not-allowed' : 'pointer' }}>
              💾 {month}월 손익관리에 반영
            </button>
            <button onClick={syncToLedger}
              disabled={payrolls.length === 0 || ledgerSyncing}
              title="직원별 급여를 거래장부에 자동 거래로 입력 (중복 방지)"
              style={{ flex: 1, minWidth: 200, padding: '10px', borderRadius: '6px',
                       border: '1.5px solid #1A56A0',
                       background: ledgerSyncing ? '#dbeafe' : '#fff',
                       color: '#1A56A0', fontSize: '13px', fontWeight: '700',
                       cursor: payrolls.length === 0 || ledgerSyncing ? 'not-allowed' : 'pointer',
                       opacity: payrolls.length === 0 ? 0.5 : 1 }}>
              {ledgerSyncing ? '⏳ 입력 중...' : '📒 거래장부 자동 입력'}
            </button>
          </div>

          <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '6px', lineHeight: 1.5 }}>
            손익관리 페이지에서 직접 수정도 가능합니다. 자동 반영 후 수동으로 조정하면 그 값이 우선.<br />
            거래장부 입력은 직원별 급여 거래 + (옵션) 사업주부담 4대보험 거래 1건. 같은 월 재실행 시 금액 업데이트.
          </div>
        </div>
      )}

      {payrolls.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', background: '#fff',
                      borderRadius: '10px', border: '1px solid #e5e7eb', color: '#9ca3af', fontSize: '13px' }}>
          이번 달 스케줄이 없습니다.
        </div>
      ) : (
        <>
          {/* 안내 힌트 + 전체 인쇄 버튼 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '12px', color: '#6b7280',
                          background: '#FAFAFA', border: '1px solid #F4F4F5',
                          borderRadius: '6px', padding: '6px 10px' }}>
              💡 직원을 클릭하면 <strong>명세서·인쇄/PDF 저장</strong>
            </div>
            <button onClick={() => setShowAllPrint(true)}
              style={{ padding: '7px 14px', borderRadius: '6px',
                       border: '1.5px solid #FF6F0F', background: '#fff',
                       color: '#18181B', fontSize: '12px', fontWeight: '700',
                       cursor: 'pointer', whiteSpace: 'nowrap' }}>
              📄 전 직원 일괄 인쇄
            </button>
          </div>

          <div style={{ background: '#fff', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            {payrolls.map((p, i) => {
              const emp = activeEmps.find(e => e.id === p.employeeId);
              return (
                <div key={p.employeeId} onClick={() => setSelectedEmpId(p.employeeId)}
                  style={{ padding: '14px 16px', cursor: 'pointer',
                           borderBottom: i < payrolls.length - 1 ? '1px solid #f3f4f6' : 'none',
                           display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                           transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafaf9'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#111' }}>
                      {p.employeeName}
                      {p.isDailyWorker && <span style={{ marginLeft: 6, fontSize: 10, color: '#EA580C', fontWeight: 700,
                                                        padding: '1px 6px', borderRadius: 4, background: '#F4F4F5' }}>일용직</span>}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>
                      {emp?.position && `${emp.position} · `}
                      {p.isDailyWorker
                        ? `${p.workDays}일 근무 · 일급 ${fmt(p.dailyRate)}원`
                        : `근무 ${p.totalHours}h`}
                      {!p.isDailyWorker && p.overtimeHours > 0 && ` · 연장 ${p.overtimeHours}h`}
                      {!p.isDailyWorker && p.weeklyHolidayHours > 0 && ` · 주휴 ${p.weeklyHolidayHours}h`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', marginRight: '8px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#18181B' }}>
                      {fmt(p.netPay)}원
                    </div>
                    <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                      📄 명세서 보기 ›
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {selectedPayroll && (
        <PayslipModal
          payroll={selectedPayroll}
          employee={selectedEmp}
          period={`${year}년 ${month}월`}
          onClose={() => setSelectedEmpId(null)}
        />
      )}

      {showAllPrint && (
        <AllPayslipsPrintView
          payrolls={payrolls}
          employees={activeEmps}
          period={`${year}년 ${month}월`}
          onClose={() => setShowAllPrint(false)}
        />
      )}
    </div>
  );
}

function AllPayslipsPrintView({ payrolls, employees, period, onClose }) {
  const handlePrint = () => {
    document.body.classList.add('semfit-print-payslip-all');
    window.print();
    setTimeout(() => document.body.classList.remove('semfit-print-payslip-all'), 200);
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
               overflow: 'auto' }}
      className="semfit-allprint-overlay">
      <div onClick={ev => ev.stopPropagation()}
        style={{ background: '#fff', borderRadius: '12px', padding: '20px',
                 maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        className="semfit-allprint-card">
        <div className="semfit-print-hide" style={{ marginBottom: '14px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#18181B', margin: '0 0 4px' }}>
            📄 전 직원 명세서 일괄 인쇄
          </h3>
          <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
            {period} · {payrolls.length}명 · 인쇄 시 직원별로 한 페이지씩 출력됨
          </p>
        </div>

        {payrolls.map((p, idx) => {
          const e = employees.find(emp => emp.id === p.employeeId);
          return (
            <div key={p.employeeId}
              className="semfit-payslip-page"
              style={{ borderBottom: idx < payrolls.length - 1 ? '2px dashed #e5e7eb' : 'none',
                       paddingBottom: '20px', marginBottom: '20px' }}>
              <div style={{ borderBottom: '2px solid #FF6F0F', paddingBottom: '10px', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                  급여 명세서 — {period}
                  <span className="semfit-print-only" style={{ marginLeft: '8px', color: '#6b7280' }}>
                    (근로기준법 제17조)
                  </span>
                </div>
                <div style={{ fontSize: '17px', fontWeight: '700', color: '#18181B', marginTop: '4px' }}>
                  {p.employeeName}
                </div>
                {e?.position && <div style={{ fontSize: '12px', color: '#6b7280' }}>{e.position}</div>}
              </div>

              <PayslipBody payroll={p} />
            </div>
          );
        })}

        <div className="semfit-print-hide" style={{ display: 'flex', gap: '6px', marginTop: '14px',
                                                     position: 'sticky', bottom: 0,
                                                     background: '#fff', paddingTop: '10px',
                                                     borderTop: '1px solid #e5e7eb' }}>
          <button onClick={handlePrint}
            style={{ flex: 1, padding: '10px', borderRadius: '8px',
                     border: '1.5px solid #FF6F0F', background: '#fff', color: '#18181B',
                     fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            🖨️ 일괄 인쇄 / PDF 저장
          </button>
          <button onClick={onClose}
            style={{ flex: 1, padding: '10px', borderRadius: '8px',
                     border: 'none', background: '#FF6F0F', color: '#fff',
                     fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            닫기
          </button>
        </div>

        <style>{`
          @media print {
            body.semfit-print-payslip-all > div:not(.semfit-allprint-overlay) {
              display: none !important;
            }
            body.semfit-print-payslip-all { background: #fff !important; }
            .semfit-allprint-overlay {
              position: static !important;
              background: #fff !important;
              padding: 0 !important;
              display: block !important;
            }
            .semfit-allprint-card {
              box-shadow: none !important;
              border-radius: 0 !important;
              max-width: none !important;
              max-height: none !important;
              overflow: visible !important;
              padding: 24px 32px !important;
            }
            .semfit-payslip-page {
              page-break-after: always;
              border-bottom: none !important;
            }
            .semfit-payslip-page:last-child { page-break-after: auto; }
            .semfit-print-hide { display: none !important; }
            .semfit-print-only { display: inline !important; }
          }
        `}</style>
      </div>
    </div>
  );
}

function PayslipBody({ payroll: p }) {
  const Row = ({ label, value, bold, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 0', fontSize: '12px',
                  borderBottom: '1px solid #f3f4f6',
                  fontWeight: bold ? '700' : '400',
                  color: color || '#374151' }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );

  return (
    <>
      {p.isDailyWorker ? (
        <>
          <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#EA580C', margin: '0 0 4px' }}>일용직 근무</h4>
          <Row label="근무일수" value={`${p.workDays}일`} />
          <Row label="일급" value={`${fmt(p.dailyRate)}원`} />
          <Row label="일 비과세 한도" value={`${fmt(p.taxFreeLimit)}원`} />
        </>
      ) : (
        <>
          <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#18181B', margin: '0 0 4px' }}>근무 시간</h4>
          <Row label="총 근무시간" value={`${p.totalHours}h`} />
          {p.overtimeHours > 0 && <Row label="연장근무" value={`${p.overtimeHours}h`} />}
          {p.nightHours > 0 && <Row label="야간근무" value={`${p.nightHours}h`} />}
          {p.holidayHours > 0 && <Row label="휴일근무" value={`${p.holidayHours}h`} />}
          {p.weeklyHolidayHours > 0 && <Row label="주휴수당 (시간)" value={`${p.weeklyHolidayHours}h`} />}
        </>
      )}

      <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#18181B', margin: '10px 0 4px' }}>지급 내역</h4>
      <Row label={p.isDailyWorker ? '일급 합계' : '기본급'} value={`${fmt(p.basePay)}원`} />
      {p.overtimePay > 0 && <Row label="연장수당" value={`${fmt(p.overtimePay)}원`} />}
      {p.nightPay > 0 && <Row label="야간수당" value={`${fmt(p.nightPay)}원`} />}
      {p.holidayPay > 0 && <Row label="휴일수당" value={`${fmt(p.holidayPay)}원`} />}
      {p.weeklyHolidayPay > 0 && <Row label="주휴수당" value={`${fmt(p.weeklyHolidayPay)}원`} />}
      <Row label="총 지급액" value={`${fmt(p.totalEarnings)}원`} bold color="#FF6F0F" />

      {p.totalDeduction > 0 && (
        <>
          <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#52525B', margin: '10px 0 4px' }}>공제 내역</h4>
          {Object.entries(p.deductions).map(([k, v]) =>
            v > 0 && <Row key={k} label={DEDUCT_LABEL[k] || k} value={`${fmt(v)}원`} />
          )}
          <Row label="총 공제" value={`${fmt(p.totalDeduction)}원`} bold color="#52525B" />
        </>
      )}

      <div style={{ background: '#FAFAFA', borderRadius: '6px', padding: '10px',
                    marginTop: '10px', textAlign: 'center' }}>
        <div style={{ fontSize: '10px', color: '#18181B' }}>실수령액</div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#18181B', marginTop: '2px' }}>
          {fmt(p.netPay)}원
        </div>
      </div>
    </>
  );
}

function PayslipModal({ payroll: p, employee: e, period, onClose }) {
  const Row = ({ label, value, bold, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', fontSize: '13px',
                  borderBottom: '1px solid #f3f4f6',
                  fontWeight: bold ? '700' : '400',
                  color: color || '#374151' }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );

  const handlePrint = () => {
    document.body.classList.add('semfit-print-payslip');
    window.print();
    setTimeout(() => document.body.classList.remove('semfit-print-payslip'), 200);
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
               overflow: 'auto' }}
      className="semfit-payslip-overlay">
      <div onClick={ev => ev.stopPropagation()}
        style={{ background: '#fff', borderRadius: '12px', padding: '20px',
                 maxWidth: '420px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        className="semfit-payslip-card">
        <div style={{ borderBottom: '2px solid #FF6F0F', paddingBottom: '12px', marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
            급여 명세서 — {period}
            <span className="semfit-print-only" style={{ marginLeft: '8px', color: '#6b7280' }}>
              (근로기준법 제17조)
            </span>
          </div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#18181B', marginTop: '4px' }}>
            {p.employeeName}
          </div>
          {e?.position && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{e.position}</div>}
        </div>

        {/* 인쇄 시 출력될 발행 정보 (화면에는 안 보임) */}
        <div className="semfit-print-only" style={{ fontSize: '10px', color: '#6b7280', marginBottom: '8px' }}>
          발행일: {new Date().toISOString().slice(0, 10)} · 셈핏 자동 생성
        </div>

        <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#18181B', margin: '0 0 6px' }}>근무 시간</h4>
        <Row label="총 근무시간" value={`${p.totalHours}h`} />
        {p.overtimeHours > 0 && <Row label="연장근무" value={`${p.overtimeHours}h`} />}
        {p.nightHours > 0 && <Row label="야간근무" value={`${p.nightHours}h`} />}
        {p.holidayHours > 0 && <Row label="휴일근무" value={`${p.holidayHours}h`} />}
        {p.weeklyHolidayHours > 0 && <Row label="주휴수당 (시간)" value={`${p.weeklyHolidayHours}h`} />}

        <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#18181B', margin: '14px 0 6px' }}>지급 내역</h4>
        <Row label="기본급" value={`${fmt(p.basePay)}원`} />
        {p.overtimePay > 0 && <Row label="연장수당" value={`${fmt(p.overtimePay)}원`} />}
        {p.nightPay > 0 && <Row label="야간수당" value={`${fmt(p.nightPay)}원`} />}
        {p.holidayPay > 0 && <Row label="휴일수당" value={`${fmt(p.holidayPay)}원`} />}
        {p.weeklyHolidayPay > 0 && <Row label="주휴수당" value={`${fmt(p.weeklyHolidayPay)}원`} />}
        <Row label="총 지급액" value={`${fmt(p.totalEarnings)}원`} bold color="#FF6F0F" />

        {p.totalDeduction > 0 && (
          <>
            <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#52525B', margin: '14px 0 6px' }}>공제 내역</h4>
            {Object.entries(p.deductions).map(([k, v]) =>
              v > 0 && <Row key={k} label={DEDUCT_LABEL[k] || k} value={`${fmt(v)}원`} />
            )}
            <Row label="총 공제" value={`${fmt(p.totalDeduction)}원`} bold color="#52525B" />
            <Row label="총 공제" value={`${fmt(p.totalDeduction)}원`} bold color="#52525B" />
          </>
        )}

        <div style={{ background: '#FAFAFA', borderRadius: '8px', padding: '12px',
                      marginTop: '14px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: '#18181B' }}>실수령액</div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: '#18181B', marginTop: '4px' }}>
            {fmt(p.netPay)}원
          </div>
        </div>

        <div className="semfit-print-only" style={{ fontSize: '10px', color: '#9ca3af',
                                                     marginTop: '12px', borderTop: '1px solid #e5e7eb',
                                                     paddingTop: '8px', textAlign: 'center' }}>
          본 명세서는 셈핏에서 자동 생성. 정확한 신고는 세무사 또는 본인 확인 필요.
        </div>

        <div className="semfit-print-hide" style={{ display: 'flex', gap: '6px', marginTop: '14px' }}>
          <button onClick={handlePrint}
            style={{ flex: 1, padding: '10px', borderRadius: '8px',
                     border: '1.5px solid #FF6F0F', background: '#fff', color: '#18181B',
                     fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            🖨️ 인쇄 / PDF 저장
          </button>
          <button onClick={onClose}
            style={{ flex: 1, padding: '10px', borderRadius: '8px',
                     border: 'none', background: '#FF6F0F', color: '#fff',
                     fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            닫기
          </button>
        </div>

        <style>{`
          .semfit-print-only { display: none; }
          @media print {
            body.semfit-print-payslip > div:not(.semfit-payslip-overlay) {
              display: none !important;
            }
            body.semfit-print-payslip { background: #fff !important; }
            .semfit-payslip-overlay {
              position: static !important;
              background: #fff !important;
              padding: 0 !important;
              display: block !important;
            }
            .semfit-payslip-card {
              box-shadow: none !important;
              border-radius: 0 !important;
              max-width: none !important;
              max-height: none !important;
              overflow: visible !important;
              padding: 24px 32px !important;
            }
            .semfit-print-hide { display: none !important; }
            .semfit-print-only { display: block !important; }
          }
        `}</style>
      </div>
    </div>
  );
}
